'use client'

import { useViewportSize } from '@mantine/hooks'
import { theme } from '@qr-front/ui'
import { Separator } from '@qr-front/ui/components/Separator'
import { SidebarProvider } from '@qr-front/ui/dashboard/components/Sidebar'
import { container, layoutTopSafePadding } from '@qr-front/ui/patterns'
import { cn, getVersionForClone, pxStringToNumber } from '@qr-front/ui/utils'
import { showNotification } from 'components/Notification'
import { BUILDER_EDIT_MODES, ROUTES } from 'constants/routesConstants'
import {
	PrivateLayoutMobileHeader,
	PrivateLayoutSidebar,
} from 'features/shared/client'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { posthogClient } from 'lib/analytics/posthog'
import { useParams, useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { ReactNode, useEffect, useLayoutEffect, useState } from 'react'
import { User } from 'types/auth'

import { AUTH_TOKEN_COOKIE } from '@/constants'
import { userAtom } from '@/global-state'
import { usePathname } from '@/i18n-routing'
import { isOnServer, THideSocials } from '@/utils'
import { getCookieClient } from '@/utils/client'

import {
	captureBuilderEvent,
	STEP_TO_INDEX,
	STEP_TO_NAME,
} from '../builderAnalytics'
import {
	activeContentTypeAtom,
	isPostSignUpVisitAtom,
	qrTypeAtom,
	startNewBuilderSession,
} from '../generatorState'
import {
	GENERATOR_SEARCH_PARAM,
	PARAM_TO_QR_TYPE,
	TQrTypeParam,
} from '../generatorTypes'
import { useCurrGeneratorStep } from '../hooks/useCurrGeneratorStep'
import { qrDesignImageErrorAtom } from '../pages/QrDesignFormPage/qrDesignState'
import { GeneratorFooter } from './GeneratorFooter/GeneratorFooter'
import { GeneratorHeader } from './GeneratorHeader/GeneratorHeader'
import { GeneratorPageHeading } from './GeneratorPageHeading'
import { GeneratorPreview } from './GeneratorPreview'
import { resolveHasGeneratorSidebar } from './resolveHasGeneratorSidebar'
import { PreviewDialog } from './utils/PreviewDialog'
import { SignUpDialog } from './utils/SignUpDialog'
import { ValidationErrorDialog } from './utils/ValidationErrorDialog'

const GeneratorLayoutContent = ({
	children,
	pathname,
	hasSidebar,
}: {
	children: ReactNode
	pathname: string
	hasSidebar: boolean
}) => {
	const pathnameMatchesTypeStep = pathname === ROUTES.generator.typeStep

	const containerCls = cn(
		'grow desktop:py-14',
		!hasSidebar ? 'py-6' : 'pb-6',
		getVersionForClone({
			base: 'bg-generator-bg',
			clone1: 'gradient-generator-bg',
		}),
	)

	return (
		<>
			{!hasSidebar && <GeneratorHeader />}
			<Separator collapse />
			<div className={containerCls}>
				<div
					className={cn(
						container,
						!hasSidebar && layoutTopSafePadding,
						'flex flex-col gap-6',
					)}
				>
					<main>
						<div
							className={cn(
								'grid grid-cols-4 gap-x-5 gap-y-4 desktop:grid-cols-[1fr_280px] desktop:gap-x-8',
							)}
						>
							<GeneratorPageHeading
								hasSidebar={hasSidebar}
								pathnameMatchesTypeStep={
									pathnameMatchesTypeStep
								}
							/>
							<div
								className={cn(
									pathnameMatchesTypeStep &&
										'pb-26 desktop:pb-0',
									'col-span-full desktop:col-start-1 desktop:col-end-2',
								)}
							>
								{children}
							</div>
							<div className="relative col-span-3 hidden flex-col items-center gap-4 desktop:col-span-1 desktop:flex">
								<div
									className={cn(
										'sticky',
										hasSidebar ? 'top-28' : 'top-36',
									)}
								>
									<GeneratorPreview
										absolutelyPositionedSwitch
									/>
								</div>
							</div>
						</div>
					</main>
				</div>
			</div>
			{!hasSidebar && <GeneratorFooter />}
		</>
	)
}

export const GeneratorLayoutClient = ({
	children,
	user,
	sessionUnauthorized,
	hideSocials,
	hidePassword,
}: {
	children: ReactNode
	user?: User | null
	sessionUnauthorized: boolean
	hideSocials: THideSocials
	hidePassword: boolean
}) => {
	const t = useTranslations()
	const pathname = usePathname()
	const searchParams = useSearchParams()
	const { width } = useViewportSize()

	const { qrType: qrTypeParam } = useParams<{ qrType: TQrTypeParam }>()
	const isDesktop = width >= pxStringToNumber(theme.screens.desktop)

	const setQrType = useSetAtom(qrTypeAtom)
	const setActiveContentType = useSetAtom(activeContentTypeAtom)
	const [qrDesignImageError, setQrDesignImageError] = useAtom(
		qrDesignImageErrorAtom,
	)
	const isPostSignUpVisit = useAtomValue(isPostSignUpVisitAtom)
	const setClientUser = useSetAtom(userAtom)
	const [hasSessionCookie] = useState(
		() => !isOnServer && Boolean(getCookieClient(AUTH_TOKEN_COOKIE)),
	)

	useEffect(() => {
		if (user) {
			setClientUser(user)
		} else if (sessionUnauthorized || !hasSessionCookie) {
			// Not signOut(): that also resets PostHog identity, which would
			// refork an anonymous visitor's ID (MYQRCODE-10838).
			setClientUser(null)
		}
	}, [setClientUser, user, sessionUnauthorized, hasSessionCookie])

	// This layout only remounts when navigating into /generator from outside
	// it (e.g. the homepage) — internal Step 1 <-> Step 2 navigation reuses the
	// same instance. Clear activeContentTypeAtom here, before ContentForm's own
	// mount effect reads it, so a fresh entry is never mistaken for "reselected
	// the same type" and content/design state from a previous visit is not
	// preserved. Needs useLayoutEffect (not useEffect) so it runs ahead of
	// ContentForm's passive effect even when landing directly on Step 2.
	useLayoutEffect(() => {
		setActiveContentType(null)
	}, [setActiveContentType])

	useEffect(() => {
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: 'smooth',
		})
	}, [pathname])

	useEffect(() => {
		if (qrTypeParam) {
			setQrType(PARAM_TO_QR_TYPE[qrTypeParam])
		}
	}, [qrTypeParam, setQrType])

	useEffect(() => {
		if (qrDesignImageError) {
			showNotification(
				t(
					'generator__design_form__logo_section__invalid_image_message',
				),
				{ type: 'error', context: 'generator' },
			)
			setQrDesignImageError(null)
		}
	}, [qrDesignImageError, setQrDesignImageError, t])

	const isEditMode =
		searchParams.get(GENERATOR_SEARCH_PARAM.Target) !== null &&
		searchParams.get(GENERATOR_SEARCH_PARAM.Edit) ===
			BUILDER_EDIT_MODES.CONTENT
	const isChangeTypeMode =
		searchParams.get(GENERATOR_SEARCH_PARAM.Target) !== null &&
		searchParams.get(GENERATOR_SEARCH_PARAM.Edit) ===
			BUILDER_EDIT_MODES.TYPE

	const pathnameStartsWithTypeStep = pathname.startsWith(
		ROUTES.generator.typeStep,
	)

	// MYQRCODE-10778 AC#13: a fresh builder_session_id per new-QR funnel entry,
	// not on edit-existing-QR entries (isEditMode/isChangeTypeMode) — this
	// layout only remounts on entry from outside /generator, so this fires
	// once per funnel, not on internal Step 1 <-> Step 2 navigation.
	useEffect(() => {
		if (pathnameStartsWithTypeStep && !isEditMode && !isChangeTypeMode) {
			const builderSessionId = startNewBuilderSession()
			posthogClient.capture('builder_funnel_start', {
				builder_session_id: builderSessionId,
				funnel_type: 'qr_create',
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const currStep = useCurrGeneratorStep()

	// MYQRCODE-10778 AC#13: keyed on currStep (not []) so it refires on every
	// internal Step 1 <-> 2 <-> 3 navigation, unlike builder_funnel_start above.
	useEffect(() => {
		captureBuilderEvent('builder_step_view', {
			step_index: STEP_TO_INDEX[currStep],
			step_name: STEP_TO_NAME[currStep],
		})
	}, [currStep])

	const hasSidebar = resolveHasGeneratorSidebar({
		user,
		hasSessionCookie,
		sessionUnauthorized,
		isPostSignUpVisit,
		pathnameStartsWithTypeStep,
		isEditMode,
		isChangeTypeMode,
	})

	if (hasSidebar) {
		return (
			<>
				<PreviewDialog />
				<SignUpDialog
					hideSocials={hideSocials}
					hidePassword={hidePassword}
					isAuthenticated={!!user}
				/>
				<ValidationErrorDialog />
				{isDesktop && !isOnServer && (
					<SidebarProvider>
						<PrivateLayoutSidebar />
						<div
							className={cn(
								'relative flex min-h-dvh w-full flex-col overflow-y-auto',
							)}
						>
							<GeneratorLayoutContent
								pathname={pathname}
								hasSidebar={hasSidebar}
							>
								{children}
							</GeneratorLayoutContent>
							<GeneratorFooter />
						</div>
					</SidebarProvider>
				)}
				{!isDesktop && !isOnServer && (
					<>
						<PrivateLayoutMobileHeader
							isEditMode={isEditMode}
							isChangeTypeMode={isChangeTypeMode}
							pathnameStartsWithTypeStep={
								pathnameStartsWithTypeStep
							}
							isDesktop={isDesktop}
						/>
						<div
							className={cn(
								'relative flex min-h-[calc(100dvh-216px)] w-full flex-col overflow-y-auto',
							)}
						>
							<GeneratorLayoutContent
								pathname={pathname}
								hasSidebar={hasSidebar}
							>
								{children}
							</GeneratorLayoutContent>
						</div>
						<GeneratorFooter />
					</>
				)}
			</>
		)
	}

	return (
		<>
			<PreviewDialog />
			<SignUpDialog
				hideSocials={hideSocials}
				hidePassword={hidePassword}
				isAuthenticated={!!user}
			/>
			<ValidationErrorDialog />
			<div className={cn('relative flex min-h-dvh flex-col')}>
				<GeneratorLayoutContent
					pathname={pathname}
					hasSidebar={hasSidebar}
				>
					{children}
				</GeneratorLayoutContent>
			</div>
		</>
	)
}
